/**
 * Global dirty-form registry — tracks every workspace currently holding
 * unsaved changes so Dialog/Sheet X clicks (and browser unloads) can prompt
 * before destroying work.
 */

export interface DirtyEntry {
  id: string;
  label: string;
  isDirty: () => boolean;
  save?: () => Promise<boolean | void>;
  discard?: () => void;
}

type Listener = () => void;

const entries = new Map<string, DirtyEntry>();
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((l) => l());

export const dirtyRegistry = {
  register(entry: DirtyEntry) {
    entries.set(entry.id, entry);
    emit();
    return () => {
      entries.delete(entry.id);
      emit();
    };
  },
  update(id: string, partial: Partial<DirtyEntry>) {
    const cur = entries.get(id);
    if (!cur) return;
    entries.set(id, { ...cur, ...partial });
    emit();
  },
  get(id: string) {
    return entries.get(id);
  },
  list() {
    return Array.from(entries.values());
  },
  anyDirty() {
    return Array.from(entries.values()).some((e) => {
      try {
        return e.isDirty();
      } catch {
        return false;
      }
    });
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

// Browser unload guard — fires on page close/refresh when any form is dirty.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (dirtyRegistry.anyDirty()) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}
